import { TypeMismatchError } from './error';
/**
 * Namespace that describes Values used in Confidence
 * @public
 */
export var Value;
(function (Value) {
    // TODO should lists be able to contain Structs?
    const LIST_ITEM_TYPES = new Set(['number', 'string', 'boolean']);
    /** Asserts a Value */
    function assertValue(value) {
        switch (typeof value) {
            case 'bigint':
            case 'symbol':
            case 'function':
                throw new TypeMismatchError('number | boolean | string | Struct | List', typeof value);
            case 'object':
                if (value === null)
                    return;
                if (Array.isArray(value)) {
                    if (value.length > 0) {
                        const itemType = getType(value[0]);
                        if (!LIST_ITEM_TYPES.has(itemType)) {
                            throw new TypeMismatchError('number | boolean | string', itemType, [0]);
                        }
                        for (let i = 1; i < value.length; i++) {
                            const type = getType(value[i]);
                            if (type !== itemType) {
                                throw new TypeMismatchError(itemType, type, [i]);
                            }
                        }
                    }
                }
                for (const key in value) {
                    if (Object.prototype.hasOwnProperty.call(value, key)) {
                        TypeMismatchError.hoist(key, () => assertValue(value[key]));
                    }
                }
                return;
            default: // no-op
        }
    }
    Value.assertValue = assertValue;
    /** Clones a Value */
    function clone(value) {
        if (value === null)
            return undefined;
        if (isStruct(value)) {
            const cloned = {};
            for (const key of Object.keys(value)) {
                cloned[key] = clone(value[key]);
            }
            return Object.freeze(cloned);
        }
        if (isList(value)) {
            return Object.freeze(value.map(clone));
        }
        return value;
    }
    Value.clone = clone;
    /** Asserts if two Values are equal */
    function equal(value1, value2) {
        if (value1 === null)
            value1 = undefined;
        if (value2 === null)
            value2 = undefined;
        if (value1 === value2)
            return true;
        const type = getType(value1);
        if (getType(value2) !== type)
            return false;
        if (type === 'Struct') {
            return structsEqual(value1, value2);
        }
        if (type === 'List') {
            return listsEqual(value1, value2);
        }
        return false;
    }
    Value.equal = equal;
    function structsEqual(struct1, struct2) {
        const keys = Object.keys(struct1);
        if (Object.keys(struct2).length !== keys.length)
            return false;
        for (const key of keys) {
            if (!equal(struct1[key], struct2[key]))
                return false;
        }
        return true;
    }
    function listsEqual(list1, list2) {
        const length = list1.length;
        if (list2.length !== length)
            return false;
        for (let i = 0; i < length; i++) {
            if (!equal(list1[i], list2[i]))
                return false;
        }
        return true;
    }
    /** Returns typename of given Value */
    function getType(value) {
        if (value === null)
            return 'undefined';
        const jsType = typeof value;
        switch (jsType) {
            case 'boolean':
            case 'number':
            case 'string':
            case 'undefined':
                return jsType;
            case 'object':
                return Array.isArray(value) ? 'List' : 'Struct';
            default: // no-op
        }
        throw new TypeError(`Invalid Value type "${jsType}"`);
    }
    Value.getType = getType;
    /** Asserts that Value is a Struct */
    function isStruct(value) {
        return Value.getType(value) === 'Struct';
    }
    Value.isStruct = isStruct;
    /** Asserts that Value is a List */
    function isList(value) {
        return Value.getType(value) === 'List';
    }
    Value.isList = isList;
    /** Asserts that type of value is Value */
    function assertType(expected, found) {
        const actual = Value.getType(found);
        if (expected !== actual) {
            throw new TypeMismatchError(expected, actual);
        }
    }
    Value.assertType = assertType;
    /** Returns a Value given a Struct */
    function get(struct, ...parts) {
        let value = struct;
        const errorPath = [];
        /* eslint-disable no-loop-func */
        for (const step of parts.flatMap(part => part.split('.'))) {
            TypeMismatchError.hoist(errorPath, () => {
                assertType('Struct', value);
                value = value[step];
                errorPath.push(step);
            });
        }
        /* eslint-enable no-loop-func */
        return value;
    }
    Value.get = get;
    /** Serializes value */
    function serialize(value) {
        const writer = new Writer();
        writer.writeValue(value);
        return writer.data;
    }
    Value.serialize = serialize;
    /** Deserializes value */
    function deserialize(data) {
        const reader = new Reader(data);
        return reader.readValue();
    }
    Value.deserialize = deserialize;
    // 8 byte buffer for encoding one float64
    const numberBuffer = new Uint16Array(4);
    class Writer {
        buffer = [];
        get data() {
            return this.buffer.join('');
        }
        // eslint-disable-next-line consistent-return
        writeValue(value) {
            // eslint-disable-next-line default-case
            switch (getType(value)) {
                case 'string':
                    return this.writeString(value);
                case 'number':
                    return this.writeNumber(value);
                case 'boolean':
                    return this.writeBoolean(value);
                case 'Struct':
                    return this.writeStruct(value);
                case 'List':
                    return this.writeList(value);
                case 'undefined':
                    // ignore
                    break;
            }
        }
        writeString(value) {
            this.buffer.push(String.fromCharCode(0 /* BinaryType.STRING */, value.length), value);
        }
        writeNumber(value) {
            new DataView(numberBuffer.buffer).setFloat64(0, value);
            this.buffer.push(String.fromCharCode(1 /* BinaryType.NUMBER */, ...numberBuffer));
        }
        writeBoolean(value) {
            this.buffer.push(String.fromCharCode(value ? 2 /* BinaryType.BOOLEAN_TRUE */ : 3 /* BinaryType.BOOLEAN_FALSE */));
        }
        writeList(list) {
            this.buffer.push(String.fromCharCode(4 /* BinaryType.LIST */, list.length));
            for (const value of list) {
                this.writeValue(value);
            }
        }
        writeStruct(struct) {
            const keys = Object.keys(struct).filter(key => struct[key] !== null && typeof struct[key] !== 'undefined');
            keys.sort();
            this.buffer.push(String.fromCharCode(5 /* BinaryType.STRUCT */, keys.length));
            for (const key of keys) {
                // naked string
                this.buffer.push(String.fromCharCode(key.length), key);
                this.writeValue(struct[key]);
            }
        }
    }
    class Reader {
        str;
        pos = 0;
        constructor(data) {
            this.str = data;
        }
        // eslint-disable-next-line consistent-return
        readValue() {
            // eslint-disable-next-line default-case
            switch (this.str.charCodeAt(this.pos)) {
                case 0 /* BinaryType.STRING */:
                    return this.readString();
                case 1 /* BinaryType.NUMBER */:
                    return this.readNumber();
                case 2 /* BinaryType.BOOLEAN_TRUE */:
                    this.pos++;
                    return true;
                case 3 /* BinaryType.BOOLEAN_FALSE */:
                    this.pos++;
                    return false;
                case 4 /* BinaryType.LIST */:
                    return this.readList();
                case 5 /* BinaryType.STRUCT */:
                    return this.readStruct();
            }
        }
        readString() {
            this.readType(0 /* BinaryType.STRING */);
            return this.readNakedString();
        }
        readNumber() {
            this.readType(1 /* BinaryType.NUMBER */);
            for (let i = 0; i < 4; i++) {
                numberBuffer[i] = this.read();
            }
            return new DataView(numberBuffer.buffer).getFloat64(0);
        }
        readBoolean() {
            const type = this.read();
            if (type === 2 /* BinaryType.BOOLEAN_TRUE */)
                return true;
            if (type === 3 /* BinaryType.BOOLEAN_FALSE */)
                return false;
            throw new Error(`Expected type ${2 /* BinaryType.BOOLEAN_TRUE */}|${3 /* BinaryType.BOOLEAN_FALSE */} found $type} at pos ${this.pos - 1}`);
        }
        readList() {
            this.readType(4 /* BinaryType.LIST */);
            const list = [];
            let len = this.read();
            while (--len >= 0) {
                list.push(this.readValue());
            }
            return list;
        }
        readStruct() {
            this.readType(5 /* BinaryType.STRUCT */);
            const struct = {};
            let len = this.read();
            while (--len >= 0) {
                const key = this.readNakedString();
                const value = this.readValue();
                struct[key] = value;
            }
            return struct;
        }
        read() {
            if (this.pos >= this.str.length)
                throw new Error('Read past end of data');
            return this.str.charCodeAt(this.pos++);
        }
        readType(expectedType) {
            const actualType = this.read();
            if (actualType !== expectedType)
                throw new Error(`Expected type ${expectedType} found ${actualType} at pos ${this.pos - 1}`);
        }
        readNakedString() {
            const len = this.read();
            const start = this.pos;
            this.pos += len;
            if (this.pos > this.str.length)
                throw new Error('Read past end of data');
            return this.str.substring(start, this.pos);
        }
    }
})(Value || (Value = {}));
//# sourceMappingURL=Value.js.map